// src/components/auth/AuthGuard.jsx
import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import { Auth } from "shared/auth";
import "./AuthGuard.css";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ✅ Module-level flag to prevent double exchange across StrictMode remounts
let isExchangingCode = false;

function AuthGuard({ children }) {
  const [authState, setAuthState] = useState("loading");
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const hasRun = useRef(false);  // ✅ Prevent double execution

  useEffect(() => {
    // ✅ Skip if already running (StrictMode double-mount protection)
    if (hasRun.current) return;
    hasRun.current = true;

    const handleAuth = async () => {
      try {
        // Step 1: Check for authorization code in URL
        const code = Auth.getCodeFromUrl();

        if (code && !isExchangingCode) {
          isExchangingCode = true;  // ✅ Lock to prevent double exchange

          // ✅ Clean URL IMMEDIATELY so second mount won't see the code
          Auth.cleanUrl();

          setAuthState("loading");

          try {
            await Auth.exchangeCode(code);
          } catch (exchangeErr) {
            console.warn("Code exchange failed:", exchangeErr.message);

            // ✅ If tokens were already saved by first mount, that's OK
            if (Auth.isAuthenticated()) {
              console.log("Tokens already present, continuing...");
            } else {
              throw exchangeErr;
            }
          } finally {
            isExchangingCode = false;  // ✅ Unlock
          }
        }

        // Step 2: Check if we have a valid token
        if (Auth.isAuthenticated()) {
          const userData = Auth.getUser();
          setUser(userData);
          setAuthState("authenticated");
        } else if (!code) {
          // No token AND no code → redirect to Midway
          setAuthState("unauthenticated");

          // ✅ Small delay so user sees the redirect message
          setTimeout(() => {
            Auth.login();
          }, 500);
        }
      } catch (err) {
        console.error("Authentication error:", err);

        // ✅ Last resort: check if tokens exist despite the error
        if (Auth.isAuthenticated()) {
          const userData = Auth.getUser();
          setUser(userData);
          setAuthState("authenticated");
        } else {
          setError(err.message);
          setAuthState("error");
        }
      }
    };

    handleAuth();
  }, []);

  // Loading
  if (authState === "loading") {
    return (
      <div className="auth-loading">
        <div className="auth-spinner"></div>
        <p>Authenticating with Midway...</p>
      </div>
    );
  }

  // Error
  if (authState === "error") {
    return (
      <div className="auth-error">
        <h2>Authentication Error</h2>
        <p>{error}</p>
        <div className="auth-error-actions">
          <button onClick={() => Auth.login()} className="auth-retry-btn">
            Sign In Again
          </button>
          <button
            onClick={() => {
              localStorage.clear();
              Auth.login();
            }}
            className="auth-retry-btn secondary"
          >
            Clear Cache & Retry
          </button>
        </div>
      </div>
    );
  }

  // Redirecting
  if (authState === "unauthenticated") {
    return (
      <div className="auth-loading">
        <div className="auth-spinner"></div>
        <p>Redirecting to Midway sign-in...</p>
      </div>
    );
  }

  // ✅ Authenticated
  return (
    <AuthContext.Provider
      value={{ user, logout: Auth.logout, getToken: Auth.getToken }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthGuard;